import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import Button from "../../components/ui/Button";

const Contact = () => {
  const { t } = useTranslation("common");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <Layout title={t("contact_us") || "Contact Us"}>
      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Contact Info */}
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md">
          <h2 className="text-h2 mb-6 text-gray-900 dark:text-white">
            {t("get_in_touch") || "Get in Touch"}
          </h2>

          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="text-blue-500 mt-1">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-h3 text-gray-900 dark:text-white">
                  {t("address") || "Address"}
                </h3>
                <p className="text-body text-gray-600 dark:text-gray-400">
                  123 Commerce St, Tech City, TC 90210
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="text-blue-500 mt-1">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-h3 text-gray-900 dark:text-white">
                  {t("email") || "Email"}
                </h3>
                <p className="text-body text-gray-600 dark:text-gray-400">
                  support@shopstore.com
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="text-blue-500 mt-1">
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-h3 text-gray-900 dark:text-white">
                  {t("phone") || "Phone"}
                </h3>
                <p className="text-body text-gray-600 dark:text-gray-400">
                  +1 (555) 123-4567
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md">
          {submitted ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 className="text-h3 text-gray-900 dark:text-white mb-2">
                {t("message_sent") || "Message Sent!"}
              </h3>
              <p className="text-body text-gray-600 dark:text-gray-400">
                {t("message_sent_desc") ||
                  "Thank you for contacting us. We will get back to you shortly."}
              </p>
              <Button
                onClick={() => setSubmitted(false)}
                variant="outline"
                className="mt-6"
              >
                {t("send_another") || "Send Another Message"}
              </Button>
            </div>
          ) : (
            <>
              <h2 className="text-h2 mb-6 text-gray-900 dark:text-white">
                {t("send_message") || "Send us a Message"}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-body-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    {t("name") || "Name"}
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-body-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    {t("email") || "Email"}
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label
                    htmlFor="message"
                    className="block text-body-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                  >
                    {t("message") || "Message"}
                  </label>
                  <textarea
                    id="message"
                    rows={4}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  ></textarea>
                </div>
                <Button type="submit" className="w-full">
                  {t("send_message_btn") || "Send Message"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Contact;
